import { GetStaticPaths, GetStaticPathsResult, GetStaticProps } from "next";
import Head from "next/head";

import { RegionParlorsContainer } from "@/containers/RegionParlorsContainer";
import {
  getSidoFacilities,
  listSidosWithCrematoria,
} from "@/utils/regionFacilities";
import { buildRegionMeta, RegionPageProps } from "@/utils/buildRegionPageProps";
import { skipDevBuildInGetStaticPaths } from "@/utils/skipDevBuildInGetStaticPaths";

/**
 * 시도 화장장 페이지.
 *
 * 경로 목록을 listSidosWithParlors 가 아니라 listSidosWithCrematoria 로 만든다.
 * 2026-08 기준 둘 다 17개로 같지만, 화장장 표면의 경로가 장례식장 데이터 변경에
 * 끌려가면 안 된다.
 */
export const getStaticPaths: GetStaticPaths = async () => {
  const skipBuildResult =
    skipDevBuildInGetStaticPaths() as GetStaticPathsResult;

  if (skipBuildResult) {
    return skipBuildResult;
  }

  return {
    paths: listSidosWithCrematoria().map((sido) => ({ params: { sido } })),
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  /**
   * 화장장을 20곳까지 담는다. 기본값 6으로는 경북(12), 경남(10), 강원(8)이
   * 잘린다. 이 페이지는 그 시도 화장장 목록이 본문이라 잘리면 안 된다.
   * /region/parlor/* 는 기본값 6 그대로다.
   */
  const data = getSidoFacilities(String(params?.sido), 20);

  if (!data) {
    return { notFound: true };
  }

  return {
    props: {
      ...buildRegionMeta(data, "crematorium"),
      pagePublishedTime: "2026-08-19T00:00:00+09:00",
      // CDN_URL이 없는 환경에서 "undefined/ogimage.png"가 og:image로 나가지 않게 한다
      ...(process.env.NEXT_PUBLIC_CDN_URL
        ? { pageOgImage: `${process.env.NEXT_PUBLIC_CDN_URL}/ogimage.png` }
        : {}),
    },
  };
};

const RegionCrematoriumSidoPage = ({
  pageJsonLd,
  ...data
}: RegionPageProps) => {
  return (
    <>
      {pageJsonLd && (
        <Head>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: pageJsonLd }}
          />
        </Head>
      )}
      <RegionParlorsContainer {...data} focus="crematorium" />
    </>
  );
};

export default RegionCrematoriumSidoPage;
